import React, { useState } from 'react';
import { toast } from 'sonner';
import { useContent } from '../../context/ContentContext';
import { DEFAULT_LOGIN_METHODS, LOGIN_PROVIDER_OPTIONS } from './SiteContentEditor';
import { EditorShell, inputCls, labelCls } from './EditorShell';
import { formatApiError } from '../../lib/api';
import { Field } from './SchemaEditor';

const normalizeMethods = (configuredMethods = []) => DEFAULT_LOGIN_METHODS.map((defaults) => {
  const configured = configuredMethods.find((method) => method.provider === defaults.provider);
  const order = Number(configured?.order);
  return {
    ...defaults,
    ...(configured || {}),
    order: Number.isInteger(order) && order > 0 ? order : defaults.order,
    logo: configured?.logo || '',
    color: configured?.color || '',
  };
});

const LoginMethodsEditor = ({ onClose }) => {
  const { content, saveLoginMethods } = useContent();
  const [methods, setMethods] = useState(() => normalizeMethods(content.site?.loginMethods));
  const [initialMethods] = useState(methods);
  const [hideMethodsPassword, setHideMethodsPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const hiddenMethods = initialMethods.filter((original) => original.enabled && !methods.find((method) => method.provider === original.provider)?.enabled);

  const updateMethod = (provider, updates, defaultSize, defaultOrder) => {
    setMethods((current) => {
      const existing = current.find((method) => method.provider === provider);
      const currentMethod = existing || { provider, enabled: false, size: defaultSize, order: defaultOrder, logo: '', color: '' };
      const next = { ...currentMethod, ...updates };
      if (updates.size && updates.size !== currentMethod.size) {
        next.order = Math.max(0, ...current.filter((method) => method.provider !== provider && method.size === updates.size).map((method) => Number(method.order) || 0)) + 1;
      }
      return existing
        ? current.map((method) => method.provider === provider ? next : method)
        : [...current, next];
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const orderedMethods = LOGIN_PROVIDER_OPTIONS.map(([provider, , size], index) => {
        const method = methods.find((entry) => entry.provider === provider);
        return method || {
          provider,
          enabled: false,
          size,
          order: LOGIN_PROVIDER_OPTIONS.slice(0, index).filter(([, , previousSize]) => previousSize === size).length + 1,
          logo: '',
          color: '',
        };
      });
      await saveLoginMethods(orderedMethods, hideMethodsPassword);
      toast.success('Innloggingsmetodane er lagra.');
      onClose();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <EditorShell title="Innloggingsmetoder" onClose={onClose} onSave={save} saving={saving} testId="login-methods-editor">
      <p className="text-sm text-white/55">Vel kva innloggingsknappar som skal visast, logo, farge og storleik. Store knappar står alltid øvst; nummeret styrer rekkefølgja innan storleiksgruppa. Dette endrar ikkje OAuth-oppsettet eller nøklane.</p>
      <div className="space-y-3">
        {LOGIN_PROVIDER_OPTIONS.map(([provider, label, defaultSize], index) => {
          const defaultOrder = LOGIN_PROVIDER_OPTIONS.slice(0, index).filter(([, , previousSize]) => previousSize === defaultSize).length + 1;
          const method = methods.find((entry) => entry.provider === provider) || { enabled: false, size: defaultSize, order: defaultOrder, logo: '', color: '' };
          return (
            <div key={provider} className="rounded-xl border border-white/10 p-4 space-y-4">
              <div className="grid grid-cols-[minmax(0,1fr)_7rem_5.5rem] items-end gap-3">
                <label aria-label={`Vis knappen for ${label}`} className="inline-flex min-w-0 items-center gap-3 pb-3 text-white font-semibold">
                  <input type="checkbox" checked={!!method.enabled} onChange={(event) => updateMethod(provider, { enabled: event.target.checked }, defaultSize, defaultOrder)} className="h-4 w-4 shrink-0 accent-[#ff2e2e]" />
                  <span className="truncate">{label}</span>
                </label>
                <div>
                  <label className={labelCls} htmlFor={`login-size-${provider}`}>Storleik</label>
                  <select id={`login-size-${provider}`} className={inputCls} value={method.size || defaultSize} onChange={(event) => updateMethod(provider, { size: event.target.value }, defaultSize, defaultOrder)}>
                    <option value="large">Stor</option>
                    <option value="small">Liten</option>
                  </select>
                </div>
                <div>
                  <label className={labelCls} htmlFor={`login-order-${provider}`}>Nr.</label>
                  <input id={`login-order-${provider}`} data-testid={`login-order-${provider}`} className={inputCls} type="number" min="1" max="99" step="1" value={method.order ?? defaultOrder} onChange={(event) => updateMethod(provider, { order: Math.min(99, Math.max(1, Number(event.target.value) || 1)) }, defaultSize, defaultOrder)} />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Logo (valfritt)</label>
                  <Field f={{ key: 'logo', label: 'Logo', type: 'image' }} value={method.logo} onChange={(logo) => updateMethod(provider, { logo }, defaultSize, defaultOrder)} />
                </div>
                <div>
                  <label className={labelCls}>Knappfarge</label>
                  <Field f={{ key: 'color', label: 'Knappfarge', type: 'color' }} value={method.color} onChange={(color) => updateMethod(provider, { color }, defaultSize, defaultOrder)} />
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {hiddenMethods.length > 0 && (
        <div>
          <label className={labelCls} htmlFor="hide-login-method-password">Godkjenningskode for å skjule {hiddenMethods.map((method) => LOGIN_PROVIDER_OPTIONS.find(([provider]) => provider === method.provider)?.[1]).filter(Boolean).join(', ')}</label>
          <input id="hide-login-method-password" data-testid="hide-login-method-password" className={inputCls} type="password" autoComplete="off" value={hideMethodsPassword} onChange={(event) => setHideMethodsPassword(event.target.value)} />
        </div>
      )}
    </EditorShell>
  );
};

export default LoginMethodsEditor;